// MermaidBlock — render mermaid code-fenced blocks as SVG diagrams.
//
// Decision (Session 19N §N4):
//   - Lazy-loaded (mermaid is ~1MB; only init when first diagram appears).
//   - Each instance gets a unique id; mermaid.render returns SVG string we inject.
//   - On error: show the raw text + an "invalid mermaid" hint (don't crash the
//     page — bad syntax in AI-generated content would otherwise blow up).
//   - Used by ResultView / EssayView / FlashcardsView via react-markdown's
//     `code` component slot (see ./markdown-with-mermaid.tsx).

import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/index.js';

let mermaidPromise: Promise<typeof import('mermaid')> | null = null;
function loadMermaid(): Promise<typeof import('mermaid')> {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((m) => {
      m.default.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'strict',
        fontFamily: 'inherit',
      });
      return m;
    });
  }
  return mermaidPromise;
}

let counter = 0;

export interface MermaidBlockProps {
  source: string;
}

export function MermaidBlock({ source }: MermaidBlockProps) {
  const t = useT();
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const idRef = useRef<string>(`mermaid-${++counter}`);

  useEffect(() => {
    let cancelled = false;
    loadMermaid().then(async (m) => {
      try {
        const { svg: rendered } = await m.default.render(idRef.current, source);
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    });
    return () => { cancelled = true; };
  }, [source]);

  if (error) {
    return (
      <div className="mermaid-block mermaid-block-error" role="img" aria-label="mermaid diagram (invalid)">
        <p className="mermaid-error-msg">{t('mermaid.renderFailed')}{error}</p>
        <pre className="mermaid-fallback-source">{source}</pre>
      </div>
    );
  }
  if (!svg) {
    return <div className="mermaid-block mermaid-block-loading">⋯</div>;
  }
  return (
    <div
      className="mermaid-block"
      role="img"
      aria-label="mermaid diagram"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
